/* PruebaBotPanel.jsx — Panel admin para vincular/desvincular el chofer ficticio
   "Prueba Octavio" que prueba el flujo del bot WhatsApp sin afectar choferes reales.

   Octavio 21-jun-2026. */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function tk() { return localStorage.getItem("dmu_token"); }
function auth() { return { headers: { Authorization: `Bearer ${tk()}` } }; }

export default function PruebaBotPanel() {
  const nav = useNavigate();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [accion, setAccion] = useState("");
  const [error, setError] = useState("");
  const [exito, setExito] = useState(null);

  const fetchEstado = async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/api/admin/prueba-bot/estado`, auth());
      setEstado(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { fetchEstado(); }, []);

  const vincular = async () => {
    if (!window.confirm("¿Vincular el chofer ficticio 'Prueba Octavio' (teléfono 6142725008)?")) return;
    setCargando(true); setAccion("vincular"); setError(""); setExito(null);
    try {
      const r = await axios.post(`${API}/api/admin/prueba-bot/vincular`, {}, auth());
      setExito(r.data);
      await fetchEstado();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCargando(false); setAccion("");
    }
  };

  const desvincular = async () => {
    if (!window.confirm("¿Borrar el chofer ficticio y TODOS sus datos generados durante la prueba (intervenciones, servicios, sesiones)?")) return;
    setCargando(true); setAccion("desvincular"); setError(""); setExito(null);
    try {
      const r = await axios.post(`${API}/api/admin/prueba-bot/desvincular`, {}, auth());
      setExito(r.data);
      await fetchEstado();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCargando(false); setAccion("");
    }
  };

  const vinculado = estado?.vinculado;
  const chofer = estado?.chofer;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="prueba-bot-panel">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => nav("/hub")}
          className="text-sm text-slate-600 hover:text-slate-900 mb-4"
          data-testid="btn-volver-hub"
        >
          ← Volver al Hub
        </button>

        <header className="mb-6">
          <div className="text-xs font-bold tracking-widest text-amber-700 uppercase mb-1">Herramientas técnicas · Admin</div>
          <h1 className="text-3xl font-bold text-slate-900">Prueba del bot WhatsApp</h1>
          <p className="text-slate-600 text-sm mt-2">
            Crea un chofer ficticio vinculado a tu número (6142725008) para validar el flujo completo del bot sin afectar choferes reales.
          </p>
        </header>

        {/* Estado actual */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Estado actual</h2>
            <button
              onClick={fetchEstado}
              className="text-xs text-slate-500 hover:text-slate-900"
              data-testid="btn-refrescar"
            >
              <i className="fa-solid fa-rotate" /> Refrescar
            </button>
          </div>

          {estado === null ? (
            <div className="text-slate-500 text-sm">Cargando…</div>
          ) : !vinculado ? (
            <div className="text-slate-700" data-testid="estado-no-vinculado">
              <span className="inline-block w-3 h-3 rounded-full bg-slate-400 mr-2" />
              No hay chofer ficticio vinculado.
            </div>
          ) : (
            <div data-testid="estado-vinculado">
              <div className="flex items-center text-emerald-700 mb-4">
                <span className="inline-block w-3 h-3 rounded-full bg-emerald-500 mr-2 animate-pulse" />
                <span className="font-semibold">Chofer ficticio ACTIVO</span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-slate-500">Nombre</dt><dd className="font-mono text-slate-900">{chofer.nombre}</dd></div>
                <div><dt className="text-slate-500">N° empleado</dt><dd className="font-mono text-slate-900">{chofer.n_empleado}</dd></div>
                <div><dt className="text-slate-500">Teléfono WhatsApp</dt><dd className="font-mono text-slate-900">{chofer.telefono}</dd></div>
                <div><dt className="text-slate-500">Pipa asignada</dt><dd className="font-mono text-slate-900">{(chofer.pipas_asignadas || []).join(", ")}</dd></div>
                <div><dt className="text-slate-500">Puntos de ruta de prueba</dt><dd className="font-mono text-slate-900">{estado.puntos_de_prueba}</dd></div>
                <div><dt className="text-slate-500">Eventos generados</dt><dd className="font-mono text-slate-900">{estado.eventos_generados}</dd></div>
                <div><dt className="text-slate-500">Servicios generados</dt><dd className="font-mono text-slate-900">{estado.servicios_generados}</dd></div>
                <div><dt className="text-slate-500">Intervenciones generadas</dt><dd className="font-mono text-slate-900">{estado.intervenciones_generadas}</dd></div>
              </dl>
            </div>
          )}
        </section>

        {/* Acciones */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <button
            onClick={vincular}
            disabled={cargando || vinculado}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-sm transition flex items-center justify-center gap-2"
            data-testid="btn-vincular"
          >
            {cargando && accion === "vincular" ? (
              <><i className="fa-solid fa-spinner fa-spin" /> Vinculando…</>
            ) : (
              <><i className="fa-solid fa-plug-circle-plus" /> Vincular Prueba Octavio</>
            )}
          </button>

          <button
            onClick={desvincular}
            disabled={cargando || !vinculado}
            className="bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-sm transition flex items-center justify-center gap-2"
            data-testid="btn-desvincular"
          >
            {cargando && accion === "desvincular" ? (
              <><i className="fa-solid fa-spinner fa-spin" /> Desvinculando…</>
            ) : (
              <><i className="fa-solid fa-trash-can" /> Desvincular y limpiar</>
            )}
          </button>
        </section>

        {/* Mensajes */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-xl p-4 mb-4" data-testid="msg-error">
            <i className="fa-solid fa-circle-exclamation mr-2" />
            <strong>Error:</strong> {error}
          </div>
        )}
        {exito && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-4 mb-4" data-testid="msg-exito">
            <i className="fa-solid fa-circle-check mr-2" />
            <strong>OK:</strong> {exito.mensaje}
            {exito.dia_ruta && (
              <div className="text-xs mt-2 font-mono">
                pipa={exito.pipa} · día={exito.dia_ruta} · puntos={exito.puntos_creados}
              </div>
            )}
          </div>
        )}

        {/* Instrucciones */}
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-sm">
          <h3 className="font-bold text-amber-900 mb-2">Cómo probar el bot tras vincular</h3>
          <ol className="space-y-1 text-amber-900 list-decimal pl-5">
            <li>Manda <code className="px-1 bg-amber-100 rounded">Hola</code> al número del bot desde tu WhatsApp.</li>
            <li>El bot te responde con el menú: <b>Continuar ruta</b> · <b>Llenado garza</b> · <b>Más opciones</b>.</li>
            <li>Prueba: Llenado garza · Ayuda bomberos · Taller · Continuar ruta · Registrar punto.</li>
            <li>Vuelve aquí y revisa que <b>eventos / servicios / intervenciones</b> crecieron.</li>
            <li>También valida en <a className="underline" href="/supervisor">Estatus de Flotilla</a> (pipa de Prueba Octavio) y <a className="underline" href="/admin/eventos-chofer">Auditoría histórica</a>.</li>
            <li>Cuando termines, regresa aquí y presiona <b>Desvincular y limpiar</b> para borrar TODO lo de la prueba.</li>
          </ol>
        </section>
      </div>
    </div>
  );
}
